// Secure storage wrapper for auth tokens.
//
// Sep 03, 2026 · Multi-Session Support (per-tab).
//
// Storage strategy:
//   1. Every browser tab receives a unique `sessionId` on first mount,
//      stashed in `sessionStorage` (native per-tab lifetime — survives
//      in-tab reloads + same-tab navigation, purged when the tab is
//      closed).
//   2. The actual JWT + user profile live in `localStorage` under
//      `mes_token::[sessionId]` and `mes_user::[sessionId]`, so two
//      tabs on the same origin can hold entirely independent sessions
//      (Administrator can be signed in as Admin in one tab and as a
//      test user in another).
//   3. On every read we JWT-decode the `exp` claim and drop the pair
//      if the token has expired — bounded XSS window, matches
//      server-side expiry, and cleanup runs per-session independently.
//   4. A best-effort sweep also purges expired tokens across ALL
//      `mes_token::*` keys so orphaned buckets left behind by closed
//      tabs don't accumulate indefinitely.
//   5. Legacy single-key data (pre-multi-session) is migrated into the
//      current tab's session on first access so users don't get
//      kicked to /login by the upgrade.

const TOKEN_PREFIX = "mes_token";
const USER_PREFIX = "mes_user";
const SESSION_ID_KEY = "mes_session_id";
const LEGACY_TOKEN_KEY = "mes_token";
const LEGACY_USER_KEY = "mes_user";

function _genId() {
  try {
    if (typeof crypto !== "undefined" && typeof crypto.randomUUID === "function") {
      return crypto.randomUUID();
    }
  } catch { /* fall through */ }
  return Math.random().toString(36).slice(2) + Date.now().toString(36);
}

function _getSessionId() {
  try {
    let sid = sessionStorage.getItem(SESSION_ID_KEY);
    if (!sid) {
      sid = _genId();
      sessionStorage.setItem(SESSION_ID_KEY, sid);
      _migrateLegacySession(sid);
    }
    return sid;
  } catch {
    // Private-mode Safari or SSR — fall back to a stable per-page id.
    return "fallback";
  }
}

function _tokenKey(sid) { return `${TOKEN_PREFIX}::${sid || _getSessionId()}`; }
function _userKey(sid)  { return `${USER_PREFIX}::${sid || _getSessionId()}`; }

// One-shot migration: if legacy `mes_token` / `mes_user` (single-key,
// pre-multi-session) exists AND this tab is a fresh session, adopt the
// legacy values into this tab so the upgrade doesn't force a re-login.
// The legacy keys are cleared after adoption so subsequent tabs don't
// inherit the same session.
function _migrateLegacySession(sid) {
  try {
    // Only adopt if the legacy key exists AND it isn't already a namespaced
    // key (a stray `mes_token` may be created by legacy call-sites — this
    // guard tolerates that).
    const legacyTok = localStorage.getItem(LEGACY_TOKEN_KEY);
    const legacyUsr = localStorage.getItem(LEGACY_USER_KEY);
    if (legacyTok && !legacyTok.startsWith("mes_token::")) {
      localStorage.setItem(_tokenKey(sid), legacyTok);
      localStorage.removeItem(LEGACY_TOKEN_KEY);
    }
    if (legacyUsr && !legacyUsr.startsWith("mes_user::")) {
      localStorage.setItem(_userKey(sid), legacyUsr);
      localStorage.removeItem(LEGACY_USER_KEY);
    }
  } catch { /* ignore */ }
}

function decodeExp(token) {
  try {
    const [, payload] = token.split(".");
    if (!payload) return 0;
    const json = JSON.parse(atob(payload.replace(/-/g, "+").replace(/_/g, "/")));
    return typeof json.exp === "number" ? json.exp * 1000 : 0;
  } catch { return 0; }
}

// Best-effort orphan sweep — purge every `mes_token::*` whose JWT has
// expired, plus the paired user blob. Runs on every read so closed-tab
// leftovers can't stack up. Cheap — localStorage is small and this only
// walks token keys.
function _purgeExpiredSessions() {
  try {
    const dead = [];
    for (let i = 0; i < localStorage.length; i++) {
      const k = localStorage.key(i);
      if (!k || !k.startsWith(TOKEN_PREFIX + "::")) continue;
      const t = localStorage.getItem(k);
      if (!t) { dead.push(k); continue; }
      const exp = decodeExp(t);
      if (exp && exp < Date.now()) dead.push(k);
    }
    for (const k of dead) {
      const sid = k.slice(TOKEN_PREFIX.length + 2);
      localStorage.removeItem(k);
      localStorage.removeItem(`${USER_PREFIX}::${sid}`);
    }
  } catch { /* ignore */ }
}

export function getToken() {
  _purgeExpiredSessions();
  const t = localStorage.getItem(_tokenKey());
  if (!t) return null;
  const exp = decodeExp(t);
  if (exp && exp < Date.now()) {
    clearAuth();
    return null;
  }
  return t;
}

export function setToken(token) {
  if (token) localStorage.setItem(_tokenKey(), token);
  else localStorage.removeItem(_tokenKey());
}

export function getUser() {
  const raw = localStorage.getItem(_userKey());
  if (!raw) return null;
  try { return JSON.parse(raw); } catch { return null; }
}

export function setUser(u) {
  if (u) localStorage.setItem(_userKey(), JSON.stringify(u));
  else localStorage.removeItem(_userKey());
}

export function clearAuth() {
  const sid = _getSessionId();
  localStorage.removeItem(_tokenKey(sid));
  localStorage.removeItem(_userKey(sid));
}

// Test / diagnostic helper — exposed for the browser console so ops can
// inspect the live session buckets without opening dev-tools > Storage.
export function _debugListSessions() {
  const rows = [];
  for (let i = 0; i < localStorage.length; i++) {
    const k = localStorage.key(i);
    if (!k || !k.startsWith(TOKEN_PREFIX + "::")) continue;
    const sid = k.slice(TOKEN_PREFIX.length + 2);
    const exp = decodeExp(localStorage.getItem(k) || "");
    rows.push({ sessionId: sid, isCurrentTab: sid === _getSessionId(), expiresAt: exp ? new Date(exp).toISOString() : "n/a" });
  }
  return rows;
}
